import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTraduzioni } from '../../i18n';
import { useRaddrizza } from './Raddrizza';
import { eFoto } from '../../utils/raddrizza';
import './raddrizza.css';

// I due bottoni per mandare un documento — «Carica» (file, galleria, computer) e «Fotografa» (fotocamera) — uguali
// in tutte le pagine, cliente e staff. Dentro c'è tutto quello che prima era sparso o mancava:
//  * ogni foto, da dove arrivi, passa dall'anteprima per raddrizzarla (prima solo quelle di «Fotografa»);
//  * più foto di seguito, una alla volta, e alla fine una busta sola;
//  * `comePdf` acceso quando almeno una foto è stata raddrizzata (il server ne fa un PDF);
//  * i file senza tipo MIME (certe fotocamere Android) riconosciuti dall'estensione, non buttati via;
//  * quando una foto non si apre (HEIC su Chrome, file rovinato) si dice e si manda com'è.
//
//   <BottoniCarica
//     onFile={(files, opzioni) => …}   // opzioni: { comePdf: true } se almeno una foto è stata raddrizzata
//     multiplo={true}
//     etichette={{ file: 'Carica', foto: 'Fotografa' }}
//     comeStaff={false}
//   />

export const ACCETTA_CLIENTE = 'application/pdf,image/jpeg,image/png,image/heic,image/heif,.heic,.heif';
export const ACCETTA_STAFF = 'application/pdf,image/*,.heic,.heif,.doc,.docx';

// La coda del caricamento: le foto passano dall'anteprima una alla volta (raddrizzamento), poi parte una busta
// sola con `comePdf` acceso se almeno una è stata raddrizzata. La usano i bottoni qui sotto e, nella pagina del
// cliente, anche i file trascinati sulla zona di caricamento: la strada è una sola, non una copia.
// `fotocamera` è l'input della fotocamera, per "Rifai"; senza (i file trascinati) "Rifai" lascia perdere quello
// scatto e manda il resto.
export const useCodaCarica = (onFile, { raddrizza = true, fotocamera = null } = {}) => {
  const raccolte = useRef([]); // i file pronti per la busta
  const avvisi = useRef([]); // le foto che non si sono potute raddrizzare
  const sospese = useRef(0); // quante foto sono ancora nell'anteprima
  const rifare = useRef(false); // "Rifai": si aspetta la foto nuova prima di mandare
  const mandaOra = useRef(null);

  const manda = () => {
    const files = raccolte.current;
    const motivi = avvisi.current;
    raccolte.current = []; avvisi.current = []; sospese.current = 0; rifare.current = false;
    if (motivi.length) toast.error(motivi[0]); // se sono tante, basta dirlo una volta
    if (files.length) onFile(files, { comePdf: files.some((f) => f.raddrizzata) });
  };
  mandaOra.current = manda;

  const pronta = (f, motivo) => {
    raccolte.current.push(f);
    if (motivo) avvisi.current.push(motivo);
    sospese.current = Math.max(0, sospese.current - 1);
    if (!sospese.current && !rifare.current) manda();
  };

  const rifai = () => {
    sospese.current = Math.max(0, sospese.current - 1);
    if (!fotocamera?.current) { // niente fotocamera da riaprire: parte quello che c'è
      if (!sospese.current) manda();
      return;
    }
    rifare.current = true;
    fotocamera.current.click();
  };

  const annullato = () => { raccolte.current = []; avvisi.current = []; sospese.current = 0; rifare.current = false; };

  const { apri, vista } = useRaddrizza(pronta, rifai, annullato);

  // Se dopo "Rifai" si chiude la fotocamera senza scattare, quello che c'era già parte lo stesso
  useEffect(() => {
    const input = fotocamera?.current;
    if (!input) return undefined;
    const chiuso = () => { if (rifare.current) { rifare.current = false; if (!sospese.current) mandaOra.current(); } };
    input.addEventListener('cancel', chiuso);
    return () => input.removeEventListener('cancel', chiuso);
  }, []); // eslint-disable-line

  // I file scelti (o trascinati): le foto in anteprima, gli altri dritti nella busta
  const accetta = (files) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;
    rifare.current = false;
    const foto = raddrizza ? lista.filter(eFoto) : [];
    const altri = raddrizza ? lista.filter((f) => !eFoto(f)) : lista;
    raccolte.current = [...raccolte.current, ...altri];
    if (foto.length) {
      sospese.current += foto.length;
      apri(foto);
    } else if (!sospese.current) manda();
  };

  return { accetta, vista };
};

const BottoniCarica = ({
  onFile,
  multiplo = true,
  etichette,
  comeStaff = false,
  attesa = false,
  principale = 'file', // quale dei due è il bottone pieno: 'file', 'foto' o niente
  raddrizza = true,
  accetta,
  classe = '',
}) => {
  // le due etichette di base nella lingua di chi guarda (lo staff resta in italiano: non passa da FornitoreLingua)
  const { t } = useTraduzioni();
  const testo = { file: t('documenti.carica_bottone'), foto: t('documenti.fotografa'), ...(etichette || {}) };
  const scelta = useRef(null);
  const fotocamera = useRef(null);
  const { accetta: inCoda, vista } = useCodaCarica(onFile, { raddrizza, fotocamera });

  const scelti = (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = ''; // così ri-scegliere lo stesso file conta lo stesso
    inCoda(lista);
  };

  const classiBottone = (quale) => (comeStaff
    ? `bc-staff${principale === quale ? ' p' : ''}`
    : `btn sm${principale === quale ? ' p' : ''}${attesa && principale === quale ? ' busy' : ''}`);

  return (
    <>
      <input ref={scelta} type="file" accept={accetta || (comeStaff ? ACCETTA_STAFF : ACCETTA_CLIENTE)} multiple={multiplo}
        hidden onChange={scelti} data-testid="input-file" />
      <input ref={fotocamera} type="file" accept="image/*" capture="environment" multiple={multiplo}
        hidden onChange={scelti} data-testid="input-foto" />
      {testo.foto && (
        <button type="button" className={`${classiBottone('foto')} ${classe}`.trim()} disabled={attesa}
          onClick={() => fotocamera.current?.click()} data-testid="bottone-fotografa">{testo.foto}</button>
      )}
      {testo.file && (
        <button type="button" className={`${classiBottone('file')} ${classe}`.trim()} disabled={attesa}
          onClick={() => scelta.current?.click()} data-testid="bottone-carica">{testo.file}</button>
      )}
      {vista}
    </>
  );
};

export default BottoniCarica;
